import React, { useRef, useState } from 'react';
import {
  FaUserPlus,
  FaEnvelope,
  FaLock,
  FaUser,
  FaGoogle,
  FaArrowRight
} from 'react-icons/fa';
import { AiFillEye, AiFillEyeInvisible } from 'react-icons/ai';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import firebase from "firebase/app";
import "firebase/auth";
import "firebase/database";
import "firebase/firestore";
import { Collections } from '../types/collection';
import { useNavigate, Link } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import api from '../libs/axiosInstance';
import { enhancedPlans } from '../features/payments/enhanced-pricing-plans.type';
import { captureException } from '@sentry/react';
import { analytics } from '../features/firebase/firebase-config';
import { useUserFriendlyError } from '../components/UserFriendlyErrorToast';
import SEO from '../features/seo/seo.feature';

interface FormInputs {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

const schema = yup.object().shape({
  fullName: yup.string().required('Full Name is required'),
  email: yup.string().email('Invalid email').required('Email Address is required'),
  password: yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password')], 'Passwords must match')
    .required('Confirm Password is required'),
});

const CreateAccount: React.FC = () => {
  analytics.logEvent('page_view', {
    page_title: 'create account',
  });

  // Define structured data for better SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Create Account | PrepBestie",
    "description": "Sign up for a PrepBestie account to access AI-powered interview practice and public speaking tools",
    "publisher": {
      "@type": "Organization",
      "name": "PrepBestie",
      "logo": {
        "@type": "ImageObject",
        "url": "https://prepbestie.com/prepbestie.png"
      }
    },
    "mainContentOfPage": {
      "@type": "WebPageElement",
      "cssSelector": ".signup-container"
    },
    "potentialAction": {
      "@type": "RegisterAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://prepbestie.com/create-account"
      },
      "result": {
        "@type": "UserAccount"
      }
    }
  };

  const navigate = useNavigate();
  const [loading, toggleLoading] = useState(false);
  const toast = useRef<Toast>(null);
  const { showError } = useUserFriendlyError();

  // Toggle password visibility for each field
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: FormInputs) => {
    try {
      if (!hasAcceptedTerms) {
        toast.current?.show({
          severity: "error",
          detail: 'Please accept the terms and conditions.',
        });
        return;
      }

      toggleLoading(true);
      const { fullName, email, password, confirmPassword } = data;

      if (password !== confirmPassword) {
        toggleLoading(false);
        toast.current?.show({
          severity: "error",
          detail: 'Password does not match confirm password.',
        });
        return;
      }

      if (!fullName || !email || !password || !confirmPassword) {
        toggleLoading(false);
        toast.current?.show({
          severity: "error",
          detail: 'Please fill the form correctly.',
        });
        return;
      }

      // Create user with email and password
      await firebase.auth().createUserWithEmailAndPassword(email, password);
      const currentUserId = firebase.auth().currentUser?.uid;

      // Update user profile
      await firebase.auth().currentUser?.updateProfile({
        displayName: fullName,
      });

      // Save additional details to Firestore
      await firebase.firestore().collection(Collections.Users).doc(currentUserId).set({
        fullName,
        email,
        createdAt: new Date().toDateString(),
        lastPractice: [],
      });

      // External API calls for payment and welcome email
      const response = await api.post('/payment/user/create', {
        email,
        name: fullName,
        userId: currentUserId,
      });

      await api.get(`/account/welcome/${currentUserId}`);

      if ([200, 201].includes(response.status)) {
        await firebase.firestore()
          .collection(Collections.Users)
          .doc(firebase.auth().currentUser?.uid)
          .update({
            subscription: enhancedPlans[0],
            subscriptionId: null,
          });
        toggleLoading(false);
        navigate('/dashboard');
      }
    } catch (error: firebase.auth.AuthError | unknown) {
      toggleLoading(false);
      captureException(error)
      handleSignUpError(error as firebase.auth.AuthError);
    }
  };

  const handleSignUpError = (error: firebase.auth.AuthError) => {
    let errorMessage = 'Unable to create an account. Please try again or reach out to support.';
    switch (error.code) {
      case 'auth/email-already-in-use':
        errorMessage = 'The email address is already in use.';
        break;
      case 'auth/invalid-email':
        errorMessage = 'The email address is not valid.';
        break;
      case 'auth/weak-password':
        errorMessage = 'The password is too weak. Please use a stronger password.';
        break;
      default:
        errorMessage = 'Unable to create an account. Please try again.';
        break;
    }
    // Use user-friendly error handling for authentication errors
    const authError = new Error(errorMessage);
    (authError as any).code = 'auth/signup-failed';
    showError(authError, {
      action: 'create-account'
    }, toast);
  };

  // Google sign-in logic
  const googleProvider = new firebase.auth.GoogleAuthProvider();
  const handleGoogleSignIn = async () => {
    try {
      if (!hasAcceptedTerms) {
        toast.current?.show({
          severity: "error",
          detail: 'Please accept the terms and conditions.',
        });
        return;
      }
      toggleLoading(true);
      const result = await firebase.auth().signInWithPopup(googleProvider);
      const user = result.user;
      if (!user) {
        toggleLoading(false);
        toast.current?.show({
          severity: "error",
          detail: 'No user information retrieved.',
        });
        return;
      }
      const userId = user.uid;
      const fullName = user.displayName;
      const email = user.email;
      await firebase.firestore().collection(Collections.Users)
        .doc(userId)
        .set(
          {
            fullName,
            email,
            createdAt: new Date().toDateString(),
            lastPractice: [],
          },
          { merge: true }
        );
      const response = await api.post('/payment/user/create', {
        email,
        name: fullName,
        userId: userId,
      });
      await api.get(`/account/welcome/${userId}`);
      if ([200, 201].includes(response.status)) {
        await firebase.firestore()
          .collection(Collections.Users)
          .doc(firebase.auth().currentUser?.uid)
          .update({
            subscription: enhancedPlans[0],
            subscriptionId: null,
          });
        toggleLoading(false);
       //  navigate('/onboarding');
        navigate('/dashboard');
      }
    } catch (error: firebase.auth.AuthError | unknown) {
      toggleLoading(false);
      captureException(error)
      // Use user-friendly error handling for Google auth errors
      showError(error, {
        action: 'google-signin'
      }, toast);
    }
  };

  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.1,
        staggerChildren: 0.1
      }
    }
  };

  const buttonVariants = {
    idle: { scale: 1 },
    hover: { scale: 1.03, transition: { duration: 0.3 } },
    tap: { scale: 0.97, transition: { duration: 0.15 } }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-700 via-indigo-800 to-purple-900 flex items-center justify-center p-4 sm:p-6">
      <SEO
        title='Create Account | PrepBestie'
        description='Sign up for PrepBestie to start practicing interviews and public speaking with AI-powered feedback, personalized recommendations, and real-time analysis to boost your confidence and communication skills.'
        name='PrepBestie'
        type='website'
        structuredData={structuredData}
        keywords={[
          'Prep Bestie',
          'PrepBestie',
          'PrepBestie account',
          'PrepBestie sign up',
          'Create account',
          'Sign up',
          'Register',
          'Interviews',
          'Interview preparation',
          'CV builder',
          'Resume improvement',
          'Job interviews',
          'Pitch preparation',
          'Public speech',
          'AI',
          'AI tools',
          'Speech analysis',
          'Communication skills',
          'Free account'
        ]}
      />
      <Toast ref={toast} />

      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute top-1/4 left-0 w-64 h-64 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute top-1/3 right-0 w-72 h-72 bg-yellow-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-pink-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      <div className="w-full max-w-md relative z-10">
        <motion.div
          className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl overflow-hidden signup-container"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="relative pb-1">
            {/* Header with Gradient */}
            <div className="relative p-8 pb-6">
              <motion.div
                className="flex flex-col items-center justify-center"
                initial="hidden"
                animate="visible"
                variants={staggerContainer}
              >
                <motion.div
                  className="w-16 h-16 flex items-center justify-center rounded-full bg-gradient-to-tr from-indigo-500 to-indigo-700 mb-4 shadow-lg"
                  variants={fadeIn}
                >
                  <FaUserPlus className="text-white text-3xl" />
                </motion.div>
                <motion.h2
                  className="text-2xl sm:text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-indigo-100 mb-2"
                  variants={fadeIn}
                >
                  Create Your Account
                </motion.h2>
                <motion.p
                  className="text-white/70 text-sm text-center max-w-xs"
                  variants={fadeIn}
                >
                  Join PrepBestie and start improving your communication skills
                </motion.p>
              </motion.div>
            </div>

            {/* Form Container */}
            <div className="bg-white rounded-tl-3xl rounded-tr-3xl p-6 sm:p-8">
              <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
                {/* Full Name */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                >
                  <label className="block text-gray-700 text-sm font-medium mb-1">Full Name</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaUser className="text-gray-400" />
                    </div>
                    <input
                      {...register('fullName')}
                      type="text"
                      className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:outline-none ${errors.fullName
                          ? 'border-red-300 focus:border-red-500 focus:ring focus:ring-red-200'
                          : 'border-gray-300 focus:border-indigo-500 focus:ring focus:ring-indigo-200'
                        } transition-all duration-200`}
                      placeholder="John Doe"
                    />
                  </div>
                  <AnimatePresence>
                    {errors.fullName && (
                      <motion.p
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="text-red-500 text-xs mt-1"
                      >
                        {errors.fullName.message}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </motion.div>

                {/* Email */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                >
                  <label className="block text-gray-700 text-sm font-medium mb-1">Email Address</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaEnvelope className="text-gray-400" />
                    </div>
                    <input
                      {...register('email')}
                      type="email"
                      className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:outline-none ${errors.email
                          ? 'border-red-300 focus:border-red-500 focus:ring focus:ring-red-200'
                          : 'border-gray-300 focus:border-indigo-500 focus:ring focus:ring-indigo-200'
                        } transition-all duration-200`}
                      placeholder="you@example.com"
                    />
                  </div>
                  <AnimatePresence>
                    {errors.email && (
                      <motion.p
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="text-red-500 text-xs mt-1"
                      >
                        {errors.email.message}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </motion.div>

                {/* Password */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.5 }}
                >
                  <label className="block text-gray-700 text-sm font-medium mb-1">Password</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaLock className="text-gray-400" />
                    </div>
                    <input
                      {...register('password')}
                      type={showPassword ? 'text' : 'password'}
                      className={`w-full pl-10 pr-10 py-3 border rounded-xl focus:outline-none ${errors.password
                          ? 'border-red-300 focus:border-red-500 focus:ring focus:ring-red-200'
                          : 'border-gray-300 focus:border-indigo-500 focus:ring focus:ring-indigo-200'
                        } transition-all duration-200`}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      {showPassword ? <AiFillEyeInvisible className="text-lg" /> : <AiFillEye className="text-lg" />}
                    </button>
                  </div>
                  <AnimatePresence>
                    {errors.password && (
                      <motion.p
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="text-red-500 text-xs mt-1"
                      >
                        {errors.password.message}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </motion.div>

                {/* Confirm Password */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.5 }}
                >
                  <label className="block text-gray-700 text-sm font-medium mb-1">Confirm Password</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaLock className="text-gray-400" />
                    </div>
                    <input
                      {...register('confirmPassword')}
                      type={showConfirmPassword ? 'text' : 'password'}
                      className={`w-full pl-10 pr-10 py-3 border rounded-xl focus:outline-none ${errors.confirmPassword
                          ? 'border-red-300 focus:border-red-500 focus:ring focus:ring-red-200'
                          : 'border-gray-300 focus:border-indigo-500 focus:ring focus:ring-indigo-200'
                        } transition-all duration-200`}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      {showConfirmPassword ? <AiFillEyeInvisible className="text-lg" /> : <AiFillEye className="text-lg" />}
                    </button>
                  </div>
                  <AnimatePresence>
                    {errors.confirmPassword && (
                      <motion.p
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="text-red-500 text-xs mt-1"
                      >
                        {errors.confirmPassword.message}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </motion.div>

                {/* Terms and Conditions */}
                <motion.div
                  className="flex items-start space-x-3 mt-2"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6, duration: 0.5 }}
                >
                  <div className="flex items-center h-5">
                    <input
                      type="checkbox"
                      id="termsCheckbox"
                      checked={hasAcceptedTerms}
                      onChange={(e) => setHasAcceptedTerms(e.target.checked)}
                      className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                  </div>
                  <label htmlFor="termsCheckbox" className="text-sm text-gray-600">
                    I accept the{' '}
                    <Link
                      to="/terms-and-conditions"
                      className="text-indigo-600 hover:text-indigo-800 font-medium hover:underline"
                    >
                      Terms and Conditions
                    </Link>
                  </label>
                </motion.div>

                {/* Submit Button */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7, duration: 0.5 }}
                >
                  <motion.button
                    type="submit"
                    className={`w-full py-3 px-6 flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-600 to-indigo-800 hover:from-indigo-700 hover:to-indigo-900 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 ${loading ? 'opacity-80' : ''}`}
                    disabled={loading}
                    variants={buttonVariants}
                    initial="idle"
                    whileHover="hover"
                    whileTap="tap"
                  >
                    {loading ? (
                      <div className="flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        <span>Creating account...</span>
                      </div>
                    ) : (
                      <>
                        <span>Create Account</span>
                        <FaArrowRight className="text-xs transition-transform group-hover:translate-x-1" />
                      </>
                    )}
                  </motion.button>
                </motion.div>
              </form>

              {/* Divider */}
              <motion.div
                className="flex items-center my-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8, duration: 0.5 }}
              >
                <div className="flex-1 h-px bg-gray-300"></div>
                <div className="px-4 text-sm text-gray-500">or sign up with</div>
                <div className="flex-1 h-px bg-gray-300"></div>
              </motion.div>

              {/* Social Login */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9, duration: 0.5 }}
              >
                <motion.button
                  type="button"
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-3 bg-white border border-gray-300 rounded-xl py-3 px-6 text-gray-700 hover:bg-gray-50 hover:shadow-md transition-all duration-300"
                  variants={buttonVariants}
                  initial="idle"
                  whileHover="hover"
                  whileTap="tap"
                >
                  <FaGoogle className="text-red-500" />
                  <span>Sign up with Google</span>
                </motion.button>
              </motion.div>

              {/* Sign In Link */}
              <motion.p
                className="text-center text-gray-600 mt-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.9, duration: 0.5 }}
              >
                Already have an account?{' '}
                <Link to="/login" className="text-indigo-600 hover:text-indigo-800 hover:underline transition-colors font-medium">
                  Sign in
                </Link>
              </motion.p>
            </div>
          </div>
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.5 }}
          className="mt-8 text-center"
        >
          <p className="text-xs text-white/50">
            &copy; {new Date().getFullYear()} PrepBestie. All rights reserved.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default CreateAccount;
